import styled, { keyframes } from 'styled-components';
import { Typography, Button, Card, Badge } from 'antd';
import { COLORS } from '../../constants/colors';

const { Title, Text } = Typography;

// Keyframe animations for enhanced UI
const shimmer = keyframes`
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
`;

export const PageContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  
  /* Mobile section titles */
  @media (max-width: 768px) {
    .ant-typography h3 {
      font-size: 16px !important;
      margin-bottom: 12px !important;
    }
  }
  
  /* Animation Keyframes */
  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(30px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  @keyframes fadeInDown {
    from {
      opacity: 0;
      transform: translateY(-20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  @keyframes slideInLeft {
    from {
      opacity: 0;
      transform: translateX(-30px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }
  
  @keyframes scaleIn {
    from {
      opacity: 0;
      transform: scale(0.9);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }
  
  /* Welcome Section Animations */
  .animate-in {
    animation: fadeInDown 0.6s ease-out forwards;
  }
  
  .animate-out {
    opacity: 0;
    transform: translateY(-20px);
  }
  
  /* Stats Row Animations */
  .stats-animate-in {
    .ant-col {
      animation: fadeInUp 0.6s ease-out forwards;
      opacity: 0;
      transform: translateY(30px);
    }
  }
  
  .stats-animate-out {
    .ant-col {
      opacity: 0;
      transform: translateY(30px);
    }
  }
  
  /* Header Animations */
  .header-animate-in {
    animation: fadeInDown 0.6s ease-out forwards;
    opacity: 0; /* Start with opacity 0 */
  }
  
  .header-animate-out {
    opacity: 0;
    transform: translateY(-20px);
  }

  /* Actions Row Animations */
  .actions-animate-in {
    /* Make all action items visible with a gentle stagger; no hard limit on count */
    .ant-col, & > * {
      opacity: 0;
      animation: fadeInUp 0.6s ease-out forwards;
    }

    .ant-col:nth-child(1), & > *:nth-child(1) { animation-delay: 0.1s; }
    .ant-col:nth-child(2), & > *:nth-child(2) { animation-delay: 0.2s; }
    .ant-col:nth-child(3), & > *:nth-child(3) { animation-delay: 0.3s; }
    .ant-col:nth-child(4), & > *:nth-child(4) { animation-delay: 0.4s; }
    .ant-col:nth-child(5), & > *:nth-child(5) { animation-delay: 0.5s; }
    .ant-col:nth-child(6), & > *:nth-child(6) { animation-delay: 0.6s; }
    .ant-col:nth-child(7), & > *:nth-child(7) { animation-delay: 0.7s; }
    .ant-col:nth-child(8), & > *:nth-child(8) { animation-delay: 0.8s; }
    .ant-col:nth-child(9), & > *:nth-child(9) { animation-delay: 0.9s; }
    .ant-col:nth-child(10), & > *:nth-child(10) { animation-delay: 1.0s; }
    .ant-col:nth-child(11), & > *:nth-child(11) { animation-delay: 1.1s; }
    .ant-col:nth-child(12), & > *:nth-child(12) { animation-delay: 1.2s; }
  }
  
  .actions-animate-out {
    .ant-col, & > * {
      opacity: 0;
      transform: translateY(30px);
    }
  }
  
  /* Divider Animation */
  .divider-animate-in {
    animation: scaleIn 0.4s ease-out forwards;
    animation-delay: 0.05s;
  }
  
  .divider-animate-out {
    opacity: 0;
    transform: scale(0.8);
  }
  
  /* Date Filter Select Styles */
  .date-filter-select {
    .ant-select-selector {
      border-color: ${COLORS.border} !important;
      
      &:hover {
        border-color: ${COLORS.$primaryBlue} !important;
      }
    }
    
    &.ant-select-focused .ant-select-selector {
      border-color: ${COLORS.$primaryBlue} !important;
      box-shadow: 0 0 0 2px ${COLORS.$primaryBlue}26 !important;
    }
  }
`;

export const WelcomeSection = styled.div`
  /* Negative top margin cancels ContentScrollArea's 24px top padding so the
     grid pattern sits flush against the top bar (and visually flows under it). */
  margin: -24px 0 24px;
  padding: 32px 4px 28px;
  background: transparent;
  border: none;
  border-radius: 0;
  color: #0f172a;
  position: relative;
  overflow: visible;

  /* Sleek grid pattern that fades from the top down — spans full viewport width.
     Starts at the very top of the scroll area (under the top bar) and extends
     further down behind the welcome content. */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 100vw;
    height: 300px;
    background-image: linear-gradient(rgba(15, 23, 42, 0.10) 1px, transparent 1px),
                      linear-gradient(90deg, rgba(15, 23, 42, 0.10) 1px, transparent 1px);
    background-size: 28px 28px;
    background-position: -1px -1px;
    -webkit-mask-image: linear-gradient(to bottom, #000 0%, rgba(0, 0, 0, 0.55) 60%, transparent 100%);
            mask-image: linear-gradient(to bottom, #000 0%, rgba(0, 0, 0, 0.55) 60%, transparent 100%);
    pointer-events: none;
    z-index: 0;
  }

  /* Animation support */
  animation-fill-mode: both;
  transition: all 0.3s ease;

  @media (max-width: 1024px) {
    padding: 4px 0 16px;
    margin-bottom: 16px;
  }

  .ant-typography {
    color: #0f172a !important;

    @media (max-width: 768px) {
      &.ant-typography h2 {
        font-size: 18px !important;
        margin-bottom: 4px !important;
        line-height: 1.2 !important;
      }
    }
  }

  /* Heartbeat animation */
  @keyframes heartbeat {
    0% {
      transform: scale(1);
    }
    14% {
      transform: scale(1.1);
    }
    28% {
      transform: scale(1);
    }
    42% {
      transform: scale(1.1);
    }
    70% {
      transform: scale(1);
    }
  }

  /* Heart throb animation - slow consistent pulse */
  @keyframes heartThrob {
    0% {
      transform: scale(1);
      opacity: 1;
    }
    50% {
      transform: scale(1.15);
      opacity: 0.9;
    }
    100% {
      transform: scale(1);
      opacity: 1;
    }
  }
`;

export const StatisticCard = styled(Card)`
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.9) 100%);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.8);
  border-radius: 16px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  min-height: 200px;
  
  /* Animation support */
  animation-fill-mode: both;
  
  /* Subtle gradient border on hover */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: 16px;
    padding: 2px;
    background: linear-gradient(135deg, transparent 0%, ${COLORS.$mainTiffany}40 50%, transparent 100%);
    -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    opacity: 0;
    transition: opacity 0.4s ease;
    pointer-events: none;
  }
  
  /* Shimmer effect overlay */
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      90deg,
      transparent 0%,
      rgba(255, 255, 255, 0.4) 50%,
      transparent 100%
    );
    background-size: 200% 100%;
    opacity: 0;
    transition: opacity 0.3s ease;
    pointer-events: none;
  }
  
  &:hover {
    transform: translateY(-6px) scale(1.02);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.12), 0 8px 16px rgba(0, 0, 0, 0.08);
    border-color: ${COLORS.$mainTiffany}50;
    
    &::before {
      opacity: 1;
    }
    
    &::after {
      opacity: 1;
      animation: ${shimmer} 2s infinite;
    }
  }
  
  .ant-card-body {
    padding: 24px;
    position: relative;
    z-index: 1;
    
    @media (max-width: 768px) {
      padding: 16px;
    }
  }
  
  /* Stat value styling */
  .stat-value {
    color: ${COLORS.textDark};
    font-size: 36px;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
    line-height: 1;
    margin-left: 16px;
    
    @media (max-width: 768px) {
      font-size: 28px;
      margin-left: 12px;
    }
  }
  
  .stat-label {
    color: ${COLORS.textMedium};
    font-size: 14px;
    font-weight: 500;
    letter-spacing: 0.01em;
    display: block;
    margin-bottom: 8px;
    
    @media (max-width: 768px) {
      font-size: 12px;
      line-height: 1.3;
    }
  }
  
  .ant-statistic-title {
    color: ${COLORS.textMedium};
    font-size: 14px;
    margin-bottom: 8px;
    font-weight: 500;
  }
  
  .ant-statistic-content {
    color: ${COLORS.textDark};
    
    .ant-statistic-content-value {
      font-weight: 700;
    }
  }
  
  @media (max-width: 768px) {
    border-radius: 12px;
  }
`;

export const ActionCard = styled(Card)`
  background: ${COLORS.gradient.subtleGray};
  border: 1px solid ${COLORS.border};
  border-radius: 12px;
  box-shadow: 0 4px 12px ${COLORS.cardShadow};
  
  /* Animation support */
  animation-fill-mode: both;
  
  .ant-card-head {
    background: #111113;
    border-bottom: 2px solid ${COLORS.$mainRed};
    border-radius: 12px 12px 0 0;
    
    .ant-card-head-title {
      color: ${COLORS.textWhite};
      font-weight: 600;
    }
  }
  .ant-btn-primary {
    background: #1890ff;
    border: none;
    border-radius: 8px;
    font-weight: 500;
    width: 100%;
    height: 48px;
    box-shadow: none !important;
    
    &:hover {
      background: #40a9ff;
      transform: translateY(-1px);
    }
  }
    .ant-btn:not(.ant-btn-primary) {
    border: 1px solid #1890ff;
    color: #1890ff;
    border-radius: 8px;
    font-weight: 500;
    width: 100%;
    height: 48px;
    box-shadow: none !important;
    
    &:hover {
      background: #1890ff;
      color: ${COLORS.textWhite};
      border-color: #1890ff;
    }
  }
`;

export const StyledTitle = styled(Title)`
  &&& {
    margin: 0;
  }
`;

export const WelcomeText = styled(Text)`
  &&& {
    font-size: 16px;
    color: #475569;
    opacity: 1;

    @media (max-width: 768px) {
      font-size: 14px;
    }
  }
`;

/**
 * EKGPulse — single-line electrocardiogram trace.
 *
 * "Snake" effect: a short visible segment (the head) travels across the
 * path while the tail erases right behind it. Achieved with a
 * stroke-dasharray of "<visible> <gap>" where the gap is larger than the
 * total path length, then animating stroke-dashoffset from positive
 * (segment off-screen left) to deeply negative (segment off-screen right).
 *
 * Tweak knobs:
 *   --ekg-duration : speed of one full sweep
 *   --ekg-length   : length of the visible segment (out of pathLength 1000)
 *   --ekg-color    : trace colour
 *   --ekg-glow     : drop-shadow glow colour
 */
export const EKGPulse = styled.div`
  --ekg-color: #0f172a;
  --ekg-glow: rgba(15, 23, 42, 0.35);
  --ekg-duration: 2.8s;
  --ekg-length: 200;

  width: 160px;
  height: 88px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: 24px;
  position: relative;

  svg {
    width: 100%;
    height: 100%;
    overflow: visible;
  }

  .ekg-baseline {
    display: none;
  }

  .ekg-track {
    fill: none;
    stroke: var(--ekg-color);
    stroke-width: 2.25;
    stroke-linecap: round;
    stroke-linejoin: round;
    filter: drop-shadow(0 0 3px var(--ekg-glow));
    /* visible segment then a gap larger than pathLength (1000) so only one
       segment ever shows on the path at a time */
    stroke-dasharray: var(--ekg-length) 1400;
    animation: ekgSnake var(--ekg-duration) linear infinite;
  }

  @keyframes ekgSnake {
    /* start: segment is just off the left edge */
    from { stroke-dashoffset: var(--ekg-length); }
    /* end: segment has fully passed the right edge (pathLength = 1000) */
    to   { stroke-dashoffset: -1000; }
  }

  @media (max-width: 768px) {
    width: 120px;
    height: 64px;
    margin-left: 12px;
  }
`;

export const StyledStatisticIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  transition: all 0.3s ease;
  
  .anticon {
    font-size: 28px;
  }
  
  &.heart-icon {
    .anticon {
      color: ${COLORS.$alertRed};
    }
  }
  
  &.team-icon {
    .anticon {
      color: ${COLORS.healthyGreen};
    }
  }
  
  &.file-icon {
    .anticon {
      color: ${COLORS.warningAmber};
    }
  }
  
  &.calendar-icon {
    .anticon {
      color: ${COLORS.$primaryBlue};
    }
  }
`;

export const ViewAllButton = styled(Button)`
  &&& {
    color: white !important;
    font-size: 14px;
    border-color: white;
    height: 32px;
    padding: 4px 12px;
    line-height: 1.5;
    
    &:hover, &:focus {
      color: white !important;
      opacity: 0.8;
    }
  }
`;

export const SecondaryText = styled(Text)`
  &&& {
    color: ${COLORS.textMedium};
  }
`;

export const EmptyStateContainer = styled.div`
  padding: 40px 0;
  text-align: center;
  color: ${COLORS.textMuted};
`;

export const EmptyStateIcon = styled.div`
  .anticon {
    font-size: 48px;
    color: ${COLORS.border};
    margin-bottom: 16px;
  }
`;

export const QuickActionsContainer = styled.div`
  width: 100%;
  
  .ant-space {
    width: 100%;
  }
  
  .ant-space-item {
    width: 100%;
  }
`;

// Quick Action button style - clean horizontal button
export const QuickActionTile = styled.div`
  background: #ffffff;
  border: 1px solid #e8e8e8;
  border-radius: 10px;
  padding: 12px 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  transition: all 0.25s ease;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    border-color: ${COLORS.$mainTiffany};
    box-shadow: 0 4px 16px rgba(78, 205, 196, 0.15);
    transform: translateY(-2px);
    background: linear-gradient(135deg, rgba(78, 205, 196, 0.05) 0%, rgba(255, 255, 255, 1) 100%);
  }
  
  &:active {
    transform: translateY(-1px);
  }
  
  @media (max-width: 576px) {
    width: 100%;
    padding: 14px 16px;
  }
`;

export const QuickActionIcon = styled.div`
  color: ${COLORS.$mainTiffany};
  font-size: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.25s ease;

  .anticon {
    font-size: 20px;
    color: inherit;
  }

  svg {
    color: inherit;
    width: 1em;
    height: 1em;
  }
  
  ${QuickActionTile}:hover & {
    color: ${COLORS.$darkTiffany};
    transform: scale(1.1);
  }
`;

export const QuickActionNameButton = styled(Button)`
  && {
    background: #000;
    border-color: #000;
    color: #fff;
    border-radius: 999px;
    height: 28px;
    padding: 0 16px; /* add more left/right padding to feel like a button */
    font-size: 13px;
    font-weight: 400;
    line-height: 28px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  }

  &&:hover, &&:focus {
    background: #000;
    border-color: #000;
    color: #fff;
    transform: translateY(-1px);
  }
`;

// Title text shown beside the icon
export const QuickActionTitle = styled.div`
  color: #333;
  font-size: 14px;
  font-weight: 500;
  transition: color 0.25s ease;
  
  ${QuickActionTile}:hover & {
    color: ${COLORS.$darkTiffany};
  }
`;

// Grid for Quick Actions - vertical list that wraps
export const QuickActionsGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  
  @media (max-width: 576px) {
    flex-direction: column;
    gap: 8px;
  }
`;

// Enhanced styled components for the new dashboard features
export const EnhancedStatisticCard = styled(StatisticCard)`
  .ant-statistic-content {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  
  .ant-progress {
    margin: 8px 0 4px 0;
  }
`;

export const ActivityTimeline = styled.div`
  .ant-timeline {
    margin-top: 16px;
  }
  
  .ant-timeline-item {
    padding-bottom: 20px;
  }
  
  .ant-timeline-item-content {
    margin: 0 0 0 18px;
  }
`;

export const StatusBadge = styled(Badge)`
  .ant-badge-status-dot {
    width: 8px;
    height: 8px;
  }
  
  &.status-success .ant-badge-status-dot {
    background-color: #52c41a;
  }
  
  &.status-warning .ant-badge-status-dot {
    background-color: #faad14;
  }
  
  &.status-error .ant-badge-status-dot {
    background-color: #ff4d4f;
  }
`;

export const SystemStatusCard = styled(Card)`
  background: linear-gradient(135deg, #f6ffed 0%, #f0f9ff 100%);
  border: 1px solid #b7eb8f;
  
  .ant-card-head {
    border-bottom: 1px solid #d9f7be;
  }
  
  .ant-card-body {
    padding: 16px;
  }
`;

export const HighRiskAlert = styled.div`
  margin: 24px 0;
  
  .ant-alert {
    border-radius: 8px;
    border-left: 4px solid #fa8c16;
  }
  
  .ant-alert-warning {
    background-color: #fff7e6;
    border-color: #ffd591;
  }
`;

export const MetricCard = styled(Card)`
  height: 100%;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  }
  
  .ant-card-head {
    border-bottom: 1px solid #f0f0f0;
    padding: 12px 16px;
  }
  
  .ant-card-body {
    padding: 16px;
  }
`;

export const TrendIndicator = styled.div`
  display: flex;
  align-items: center;
  font-size: 12px;
  margin-top: 4px;
  
  .anticon {
    margin-right: 4px;
  }
  
  &.trend-up {
    color: #52c41a;
  }
  
  &.trend-down {
    color: #ff4d4f;
  }
  
  &.trend-neutral {
    color: #8c8c8c;
  }
`;

// Header Components
export const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: middle;
  width: 100%;
  padding: 0 24px;
  
  /* Animation support */
  animation-fill-mode: both;
`;

export const DashboardTitle = styled.h1`
  margin: 0;
  color: ${COLORS.textDark};
  font-size: 24px;
  font-weight: 500;
  line-height: 1.3;
  letter-spacing: -0.3px;
  display: flex;
  align-items: center;
  gap: 12px;
`;

// Date Filter Section
export const DateFilterSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
  padding: 16px 20px;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 250, 252, 0.8) 100%);
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 12px;
    align-items: stretch;
    padding: 12px 16px;
  }
`;

export const DateFilterLabel = styled.span`
  font-size: 14px;
  color: ${COLORS.textMedium};
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
  
  .anticon {
    color: ${COLORS.$mainTiffany};
  }
`;

export const DateFilterButtons = styled.div`
  display: flex;
  gap: 8px;
  
  @media (max-width: 768px) {
    flex-wrap: wrap;
  }
`;

export const DateFilterButton = styled.button`
  padding: 4px 12px;
  height: 32px;
  border-radius: 6px;
  border: 1px solid ${props => props.$active ? '#1a1a1a' : COLORS.border};
  background: ${props => props.$active ? '#1a1a1a' : 'white'};
  color: ${props => props.$active ? 'white' : COLORS.textMedium};
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    border-color: #1a1a1a;
    color: ${props => props.$active ? 'white' : '#1a1a1a'};
  }
  
  @media (max-width: 768px) {
    flex: 1;
    min-width: calc(50% - 4px);
  }
`;

// Section Header
export const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  
  h3 {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    color: ${COLORS.textDark};
  }
`;

// Activity Feed Styles
export const ActivityFeed = styled(Card)`
  border-radius: 16px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
  
  .ant-card-head {
    border-bottom: 1px solid #f0f0f0;
    padding: 16px 20px;
    
    .ant-card-head-title {
      font-size: 16px;
      font-weight: 600;
    }
  }
  
  .ant-card-body {
    padding: 0;
    max-height: 400px;
    overflow-y: auto;
  }
`;

export const ActivityItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px 20px;
  border-bottom: 1px solid #f5f5f5;
  transition: background-color 0.2s ease;
  
  &:last-child {
    border-bottom: none;
  }
  
  &:hover {
    background-color: #fafafa;
  }
`;

export const ActivityIcon = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  
  &.screening {
    background: linear-gradient(135deg, rgba(227, 27, 35, 0.1) 0%, rgba(227, 27, 35, 0.05) 100%);
    color: ${COLORS.$alertRed};
  }
  
  &.queue {
    background: linear-gradient(135deg, rgba(24, 144, 255, 0.1) 0%, rgba(24, 144, 255, 0.05) 100%);
    color: ${COLORS.$primaryBlue};
  }
  
  &.event {
    background: linear-gradient(135deg, rgba(76, 175, 80, 0.1) 0%, rgba(76, 175, 80, 0.05) 100%);
    color: ${COLORS.healthyGreen};
  }
  
  &.risk {
    background: linear-gradient(135deg, rgba(255, 152, 0, 0.1) 0%, rgba(255, 152, 0, 0.05) 100%);
    color: ${COLORS.warningAmber};
  }
`;

export const ActivityContent = styled.div`
  flex: 1;
  
  .activity-title {
    font-size: 14px;
    font-weight: 500;
    color: ${COLORS.textDark};
    margin-bottom: 4px;
  }
  
  .activity-time {
    font-size: 12px;
    color: ${COLORS.textMuted};
  }
`;

// Risk Distribution Card
export const RiskCard = styled(Card)`
  border-radius: 16px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
  height: 100%;
  
  .ant-card-head {
    border-bottom: 1px solid #f0f0f0;
    padding: 16px 20px;
    
    .ant-card-head-title {
      font-size: 16px;
      font-weight: 600;
    }
  }
  
  .ant-card-body {
    padding: 20px;
  }
`;

export const RiskBar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

export const RiskLabel = styled.span`
  width: 80px;
  font-size: 13px;
  font-weight: 500;
  color: ${COLORS.textMedium};
`;

export const RiskProgress = styled.div`
  flex: 1;
  height: 8px;
  background: #f0f0f0;
  border-radius: 4px;
  overflow: hidden;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    height: 100%;
    width: ${props => props.$percent || 0}%;
    background: ${props => props.$color || COLORS.$mainTiffany};
    border-radius: 4px;
    transition: width 0.6s ease-out;
  }
`;

export const RiskCount = styled.span`
  min-width: 40px;
  text-align: right;
  font-size: 14px;
  font-weight: 600;
  color: ${COLORS.textDark};
`;

// Stats Grid for secondary stats
export const SecondaryStatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-top: 24px;
  
  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
`;

export const SecondaryStatCard = styled.div`
  background: white;
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 12px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  transition: all 0.3s ease;
  
  &:hover {
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
    transform: translateY(-2px);
  }
`;

export const SecondaryStatIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  
  &.warning {
    background: linear-gradient(135deg, rgba(255, 152, 0, 0.15) 0%, rgba(255, 152, 0, 0.05) 100%);
    color: ${COLORS.warningAmber};
  }
  
  &.info {
    background: linear-gradient(135deg, rgba(24, 144, 255, 0.15) 0%, rgba(24, 144, 255, 0.05) 100%);
    color: ${COLORS.$primaryBlue};
  }
  
  &.success {
    background: linear-gradient(135deg, rgba(76, 175, 80, 0.15) 0%, rgba(76, 175, 80, 0.05) 100%);
    color: ${COLORS.healthyGreen};
  }
  
  &.danger {
    background: linear-gradient(135deg, rgba(227, 27, 35, 0.15) 0%, rgba(227, 27, 35, 0.05) 100%);
    color: ${COLORS.$alertRed};
  }
`;

export const SecondaryStatContent = styled.div`
  .stat-value {
    font-size: 24px;
    font-weight: 700;
    color: ${COLORS.textDark};
    line-height: 1.2;
  }
  
  .stat-label {
    font-size: 13px;
    color: ${COLORS.textMuted};
    margin-top: 4px;
  }
`;

// Dashboard Grid Layout
export const DashboardGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 24px;
  margin-top: 24px;
  
  @media (max-width: 992px) {
    grid-template-columns: 1fr;
  }
`;

// ============================================
// Upcoming Events Section
// ============================================

export const EventsCard = styled.div`
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.06);
  overflow: hidden;
  height: 100%;
`;

export const EventsHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
`;

export const EventsTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 10px;
  
  svg {
    color: #1890ff;
  }
`;

export const ViewAllLink = styled.a`
  font-size: 13px;
  color: #1890ff;
  font-weight: 500;
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: all 0.2s ease;
  
  &:hover {
    color: #40a9ff;
    gap: 6px;
  }
`;

export const EventsList = styled.div`
  padding: 8px;
`;

export const EventItem = styled.div`
  display: flex;
  align-items: stretch;
  padding: 12px;
  border-radius: 12px;
  transition: all 0.2s ease;
  cursor: pointer;
  position: relative;
  margin-bottom: 4px;
  
  &:last-child {
    margin-bottom: 0;
  }
  
  &:hover {
    background: rgba(24, 144, 255, 0.04);
  }
  
  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 4px;
    height: 60%;
    border-radius: 2px;
    background: ${props => props.$color || '#64748b'};
    opacity: 0.9;
  }
`;

export const EventDateBadge = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-width: 48px;
  height: 48px;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-radius: 10px;
  margin-left: 12px;
  margin-right: 14px;
  border: 1px solid rgba(0, 0, 0, 0.04);
`;

export const EventDateMonth = styled.span`
  font-size: 10px;
  font-weight: 600;
  color: #1890ff;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  line-height: 1;
`;

export const EventDateDay = styled.span`
  font-size: 18px;
  font-weight: 700;
  color: #1e293b;
  line-height: 1.2;
`;

export const EventInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-width: 0;
`;

export const EventName = styled.h4`
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 4px 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const EventLocation = styled.span`
  font-size: 12px;
  color: #64748b;
  display: flex;
  align-items: center;
  gap: 4px;
  
  svg {
    font-size: 10px;
    opacity: 0.7;
  }
`;

const statusColors = {
  confirmed: { bg: '#e6f7ff', text: '#1890ff', border: '#91d5ff' },
  completed: { bg: '#f6ffed', text: '#52c41a', border: '#b7eb8f' },
  cancelled: { bg: '#fff2f0', text: '#ff4d4f', border: '#ffccc7' },
  'no-show': { bg: '#fff7e6', text: '#fa8c16', border: '#ffd591' },
  archived: { bg: '#f5f5f5', text: '#8c8c8c', border: '#d9d9d9' },
  pending: { bg: '#f9f0ff', text: '#722ed1', border: '#d3adf7' }
};

export const EventStatus = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: 12px;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 600;
  text-transform: capitalize;
  white-space: nowrap;
  background: ${props => statusColors[props.$status]?.bg || statusColors.pending.bg};
  color: ${props => statusColors[props.$status]?.text || statusColors.pending.text};
  border: 1px solid ${props => statusColors[props.$status]?.border || statusColors.pending.border};
`;

export const EventTypeBadge = styled.span`
  font-size: 11px;
  color: #64748b;
  background: rgba(0, 0, 0, 0.04);
  padding: 2px 8px;
  border-radius: 4px;
  margin-left: 8px;
`;

export const EmptyEvents = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  text-align: center;
  
  svg {
    font-size: 40px;
    color: #cbd5e1;
    margin-bottom: 16px;
  }
`;

export const EmptyEventsText = styled.p`
  font-size: 14px;
  color: #94a3b8;
  margin: 0;
`;
